import { colors, Menu } from '@mui/material';
import { Badge } from '@sweep-io/sweep-design';
import pluralize from 'pluralize';
import { ReactNode } from 'react';
import { getMetadataLabel } from '../../../../../documentation/tabs/getMetadataLabel';
import { CustomMenuItem } from './CustomMenuItem';
import { Check } from '@sweep-io/sweep-design/dist/icons';
import { CenteredCircularProgress } from '../../../../../common/CenteredCircularProgress';

const constructStateToMessage = ({
  loading,
  noRelationships,
  allRelationshipsAdded,
  error,
  hasRelationships,
}: {
  loading: string;
  noRelationships: string;
  allRelationshipsAdded: string;
  error: string;
  hasRelationships: string;
}): Record<string, { message: string; icon?: ReactNode }> => {
  return {
    loading: { message: loading, icon: <CenteredCircularProgress size={24} /> },
    noRelationships: { message: noRelationships },
    allRelationshipsAdded: {
      message: allRelationshipsAdded,
      icon: <Check color={colors.grey[900]} />,
    },
    error: { message: error },
    hasRelationships: { message: hasRelationships },
  };
};
export interface RelationshipsMenuViewProps<DataT = any> {
  state: 'loading' | 'error' | 'hasRelationships' | 'noRelationships' | 'allRelationshipsAdded';
  entries: { key: string; data: DataT; count: number }[];
  onEntryClick: (data: DataT) => void;
  onClose: () => void;
  anchorEl: (EventTarget & HTMLLIElement) | null;
  stateMessages: {
    loading: string;
    error: string;
    hasRelationships: string;
    noRelationships: string;
    allRelationshipsAdded: string;
  };
}

export function RelationshipsMenuView<DataT = any>({
  state,
  entries,
  onEntryClick,
  onClose,
  anchorEl,
  stateMessages,
}: RelationshipsMenuViewProps<DataT>) {
  const renderContent = () => {
    const stateToMessage = constructStateToMessage(stateMessages);
    if (state === 'hasRelationships') {
      return (
        <>
          {entries.map(({ key, data, count }) => (
            <CustomMenuItem
              key={key}
              onClick={() => {
                onEntryClick(data);
              }}
              disabled={count === 0}
              icon={<Badge variant="secondary" label={count} />}
            >
              {pluralize(getMetadataLabel(key), count)}
            </CustomMenuItem>
          ))}
        </>
      );
    }
    return (
      <CustomMenuItem disabled icon={stateToMessage[state].icon}>
        {stateToMessage[state].message}
      </CustomMenuItem>
    );
  };
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '.MuiListItemIcon-root': {
          minWidth: '24px',
        },
        '& li': {
          paddingLeft: '8px',
        },
      }}
    >
      {renderContent()}
    </Menu>
  );
}
